import styled from 'styled-components';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import { Input, Button as BaseButton } from 'styles/elements';
import { TableRow } from 'components/Table';
import { MainColumn as BaseMainColumn } from 'components/Layout';
import Card from 'components/Card';
import { EXTRA_COLUMN_BREAK, tablet } from 'styles/breakpoints';
import { rgba } from 'styles/utils/color';

export const OverviewRow = styled(TableRow)<{ tableBodyRow?: boolean }>`
  grid-template-columns: repeat(12, minmax(64px, 1fr));
  padding: 0;
`;

export const DarkPoolOverviewRow = styled(TableRow)<{ tableBodyRow?: boolean }>`
  grid-template-columns: repeat(6, minmax(64px, 1fr));
  padding: 0;
`;

export const DashboardRow = styled(TableRow)<{
  tableBodyRow?: boolean;
  isUnusual?: boolean;
  isGoldenSweep?: boolean;
}>`
  grid-template-columns:
    minmax(70px, 0.95fr) minmax(64px, 0.9fr) minmax(42px, 0.7fr) minmax(42px, 0.65fr)
    ${ENABLE_OPTION_FLOW_READ_MODE ? 'minmax(88px, 1fr)' : ''}
    minmax(72px, 1.2fr)
    minmax(54px, 0.8fr)
    minmax(44px, 0.65fr)
    minmax(52px, 0.7fr)
    minmax(56px, 1.15fr)
    minmax(64px, 1fr)
    minmax(52px, 0.8fr)
    repeat(2, minmax(64px, 1fr))
    minmax(64px, 1fr)
    minmax(54px, 0.8fr);
  min-height: ${({ tableBodyRow }) => (tableBodyRow ? 'auto' : '40px')};
  background: ${({ isUnusual, isGoldenSweep, theme }) => {
    if (isGoldenSweep) {
      return `linear-gradient(
            -90deg,
            rgba(0, 0, 0, 0) 2%,
            ${rgba(theme.color.mixedSentiment, 0.19)} 32%,
            ${rgba(theme.color.mixedSentiment, 0.14)} 74%,
            rgba(0, 0, 0, 0) 98%);`;
    }

    if (isUnusual) {
      return `linear-gradient(
        -90deg,
        rgba(0, 0, 0, 0) 2%,
        ${rgba(theme.color.unusualTrade, 0.19)} 32%,
        ${rgba(theme.color.unusualTrade, 0.14)} 74%,
        rgba(0, 0, 0, 0) 98%
      )`;
    }

    return 'transparent';
  }};
`;

export const MainColumn = styled(BaseMainColumn)<{ autoRows: number }>`
  grid-template-rows: repeat(${({ autoRows }) => autoRows}, auto) minmax(0, 100%);

  ${tablet()} {
    grid-template-rows: repeat(${({ autoRows }) => autoRows}, auto) repeat(1, minmax(0, 100%));
  }
`;

export const HighlightedCard = styled(Card)`
  border: 1px solid ${({ theme }) => theme.color.graphCall};
`;

export const TickerInput = styled(Input)<{ hasButtonAttached: boolean }>`
  margin-bottom: 0;
  font-size: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
  height: 34px;
  background-color: ${({ theme }) => theme.color.backgroundSecondary};
  color: ${({ theme }) => theme.color.textPrimary};
  font-weight: 500;
  ${({ hasButtonAttached }) =>
    hasButtonAttached
      ? `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `
      : ''};

  &:hover {
    border-color: ${({ theme }) => theme.color.buttonPrimary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.color.buttonPrimary};
    background-color: ${({ theme }) => theme.color.backgroundDistinct};
    outline: 0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    border-color: transparent;
    background-color: ${({ theme }) => theme.color.paper};
  }
`;

export const SingleRowBody = styled.div`
  background-color: ${({ theme }) => theme.color.backgroundSecondary};
`;

export const NewsItem = styled.a`
  display: block;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  padding-bottom: 0.5rem;
  margin-bottom: 0.6rem;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: none;
  }
`;

export const ChartAnalysisContent = styled.div`
  width: 96vw;
  height: 88vh;
  max-width: 1200px;
`;

export const CHART_HEADER_HEIGHT = 60;
export const ChartHeader = styled.h3`
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  line-height: 1;
  height: ${CHART_HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
`;

export const ChartContainer = styled.div`
  height: calc(100% - ${CHART_HEADER_HEIGHT}px);
`;

export const InlineSentiment = styled.span<{ sentiment: string }>`
  text-transform: uppercase;
  margin-right: 0.5rem;
  font-size: 80%;
  font-weight: 600;
  color: ${({ theme, sentiment }) => {
    if (sentiment === 'Positive') {
      return theme.color.graphCall;
    } else if (sentiment === 'Negative') {
      return theme.color.graphPut;
    } else if (sentiment === 'Neutral') {
      return theme.color.mixedSentimentBright;
    } else {
      return theme.color.textPrimary;
    }
  }};
`;

export const LayoutButtons = styled.div<{ showTall?: boolean }>`
  @media (min-width: ${EXTRA_COLUMN_BREAK}px) {
    display: ${({ showTall }) => (showTall ? 'flex' : 'none')} !important;
  }
`;

// The pane content: everything under the widget header. Sized by flex rather than a fixed
// `calc(100% - 34px)` so the header can wrap onto a second line in a narrow column (the toggle,
// seven widget buttons and the label need ~385px; the column is ~300px at 1025px) without the
// content overflowing the pane. The pane itself is a flex column.
export const LayoutButtonRow = styled.div`
  flex: 1 1 0%;
  min-height: 0;
`;

export const IconButton = styled(BaseButton)<{ isActive: boolean }>`
  padding: 0;
  height: 34px;
  width: 34px;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border-width: 1px;
  border-color: ${({ theme, isActive }) =>
    isActive ? theme.color.buttonPrimary : theme.color.border};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.color.buttonPrimary : theme.color.border};

  &:focus {
    outline: none;
  }
`;

export const FeedTypeToggle = styled(BaseButton)<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 72px;
  padding: 0;
  font-size: 10px;
  line-height: 1;
  border: 1px solid
    ${({ theme, isActive }) => (isActive ? theme.color.buttonPrimary : theme.color.border)};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.color.buttonPrimary : 'transparent'};
  border-radius: 0;
  &:not(:first-of-type) {
    margin-left: -1px;
  }
  &:first-of-type {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  &:last-of-type {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  ${({ isActive }) =>
    isActive &&
    `
      z-index: 1;
    `}
  &:focus {
    outline: none;
  }
`;

export const DarkFeedTypeToggle = styled(FeedTypeToggle)<{ isActive: boolean }>`
  padding: 0px;

  @media (max-width: 639px) {
    width: 48px;
  }
`;

export const LayoutToggle = styled(FeedTypeToggle)`
  height: 34px;
  width: 50%;
  font-size: 0.75rem;
  line-height: 1rem;
`;

export const PopoverWrapper = styled.div`
  display: none;
`;

export const InfoWrapper = styled.div`
  &:hover {
    ${PopoverWrapper} {
      display: block;
    }
  }
`;

export const WidgetWrapper = styled.div`
  background-color: ${({ theme }) => theme.color.backgroundSecondary};
  height: 100%;
`;

export const AutoTopGrid = styled.div`
  display: grid;
  grid-template-rows: minmax(0, auto) minmax(0, 100%);
`;

export const IconButtonToggle = styled(BaseButton)<{ isActive: boolean }>`
  padding: 0;
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border-width: 1px;
  border-color: ${({ theme, isActive }) =>
    isActive ? theme.color.buttonPrimary : theme.color.border};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.color.buttonPrimary : theme.color.border};

  &:focus {
    outline: none;
  }
`;
